import { FontIcon } from "@fluentui/react";
import classNames from "classnames";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { Component, type SyntheticEvent, type MouseEvent } from "react";
import { store } from "../../store";
import { CollapseIcon } from "./collapse-icon";
import css from "./section.scss";
import { type NavigationRoute } from "../../routes";
import { cn } from "@/lib/utils";

export interface SectionProps {
    readonly children?: React.ReactElement | React.ReactElement[];
    readonly title?: string;
    readonly titleAdornment?: React.ReactElement;
    readonly icon?: string;
    readonly actionButtons?: React.ReactElement;
    readonly withPadding?: boolean;
    readonly collapsible?: boolean;
    readonly defaultCollapsed?: boolean;
    readonly className?: string;
    readonly fill?: boolean;
    readonly route?: NavigationRoute;
}

@observer
export class Section extends Component<SectionProps> {
    @observable private accessor collapsed: boolean | undefined;

    constructor(props: SectionProps) {
        super(props);
        this.collapsed = this.props.defaultCollapsed;
        makeObservable(this);
    }

    @computed private get showHeader(): boolean {
        return Boolean(
            this.props.title ||
                this.props.titleAdornment ||
                this.props.icon ||
                this.props.actionButtons ||
                this.props.collapsible,
        );
    }

    @computed private get showContent(): boolean {
        return !this.props.collapsible || !this.collapsed;
    }

    @action.bound private toggleCollapsed(evt: SyntheticEvent<HTMLElement>): void {
        evt.stopPropagation();
        evt.preventDefault();
        if (!this.props.collapsible) {
            return;
        }
        this.collapsed = !this.collapsed;
    }

    @action.bound private updateURL(evt: MouseEvent): void {
        evt.preventDefault();
        evt.stopPropagation();
        if (this.props.route) {
            store.historyProvider.history.push(this.props.route.path());
        }
    }

    public render(): JSX.Element {
        return (
            <section
                className={classNames(
                    css.section,
                    this.props.className,
                    this.props.fill && css.section__fill,
                )}
            >
                {this.showHeader && (
                    <div
                        className={classNames(css.section__header, {
                            [css["section__header--clickable"]]: this.props.collapsible,
                            [css["section__header--collapsed"]]: this.collapsed,
                        })}
                        onClick={this.toggleCollapsed}
                    >
                        {this.props.collapsible && (
                            <CollapseIcon
                                collapsed={this.collapsed}
                                onClick={this.toggleCollapsed}
                            />
                        )}
                        {this.props.title && (
                            <h2
                                className={cn(
                                    css.section__title,
                                    this.props.titleAdornment && "flex items-center gap-2.5",
                                )}
                            >
                                {this.props.route ? (
                                    <a
                                        className={css.section__route}
                                        href={this.props.route.path()}
                                        onClick={this.updateURL}
                                    >
                                        {this.props.title}
                                    </a>
                                ) : (
                                    this.props.title
                                )}
                                {this.props.titleAdornment && (
                                    <span className="text-base font-normal">
                                        {this.props.titleAdornment}
                                    </span>
                                )}
                            </h2>
                        )}
                        {(this.props.icon || this.props.actionButtons) && (
                            <div className={css.section__right}>
                                {this.props.actionButtons && (
                                    <div className={css.section__actionButtons}>
                                        {this.props.actionButtons}
                                    </div>
                                )}
                                {this.props.icon && (
                                    <FontIcon
                                        className={css.section__icon}
                                        aria-label={this.props.icon}
                                        iconName={this.props.icon}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
                {this.showContent && (
                    <div
                        className={cn(
                            this.props.withPadding
                                ? css.section__content
                                : css.section__contentNoPaddings,
                            this.props.fill ? css.section__contentFill : undefined,
                        )}
                    >
                        {this.props.children}
                    </div>
                )}
            </section>
        );
    }
}
